import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ChampionTestimonialService, ResubmitChampionRequest, GsUserService, ChampionFileType } from '@gs-2.0/core';

export interface ResubmitChampionDialogData {
  organizationName: string;
  organizationUuid: string;
  championName: string;
  engagedSince: string | null;
  engagementMonth?: number;
  engagementYear?: number;
  testimonial: string;
  visibilityDuration: string;
  mediaFileName?: string;
  mediaUrl?: string;
  mediaUuid?: string;
  uuid: string;
  isEditMode?: boolean; // If true, fields are editable; if false/undefined, fields are disabled
}

export interface ResubmitChampionDialogResult {
  championName: string;
  engagedSince: string;
  testimonial: string;
  visibilityDuration: string;
  mediaFile?: File;
  action: 'resubmit' | 'draft';
}

@Component({
  selector: 'app-resubmit-champion-dialog',
  standalone: false,
  templateUrl: './resubmit-champion-dialog.component.html',
  styleUrl: './resubmit-champion-dialog.component.scss'
})
export class ResubmitChampionDialogComponent implements OnInit {
  form!: FormGroup;
  selectedFile: File | null = null;
  mediaFileName: string = '';
  mediaUrl: string = '';
  isSubmitting = false;
  isUploading = false;
  uploadedFileUuid: string = '';
  fileSize: string = '';
  isVideo = false;
  isDocument = false;

  months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  monthNameToNumber: { [key: string]: number } = {
    'January': 1, 'February': 2, 'March': 3, 'April': 4,
    'May': 5, 'June': 6, 'July': 7, 'August': 8,
    'September': 9, 'October': 10, 'November': 11, 'December': 12
  };

  years: number[] = [];
  visibilityOptions = [
    { value: '3', label: '3 months' },
    { value: '6', label: '6 months' },
    { value: '12', label: '12 months' },
    { value: '24', label: '24 months' }
  ];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ResubmitChampionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ResubmitChampionDialogData,
    private championTestimonialService: ChampionTestimonialService,
    private gsUserService: GsUserService
  ) {
    // Generate years (current year to 25 years ago)
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= currentYear - 25; i--) {
      this.years.push(i);
    }
  }

  /**
   * Extracts the filename from a media path
   * Path format: {uuid}/championHighQualityImage/{filename}.{extension}
   * Returns: {filename}.{extension}
   */
  private extractFileName(mediaPath: string): string {
    if (!mediaPath) return '';

    const parts = mediaPath.split('/');
    if (parts.length > 0) {
      return parts[parts.length - 1];
    }

    return mediaPath;
  }

  /**
   * Converts month number to month name
   * @param monthNumber Month number (1-12)
   * @returns Month name (e.g., "January")
   */
  private getMonthName(monthNumber: number): string {
    const monthIndex = monthNumber - 1;
    if (monthIndex >= 0 && monthIndex < this.months.length) {
      return this.months[monthIndex];
    }
    return '';
  }

  ngOnInit(): void {
    // Extract filename from path if mediaFileName is provided
    this.mediaFileName = this.data.mediaFileName ? this.extractFileName(this.data.mediaFileName) : '';
    this.mediaUrl = this.data.mediaUrl || '';
    this.uploadedFileUuid = this.data.mediaUuid || '';

    // Parse engaged since - prioritize numeric values from API, fall back to string parsing
    let engagedMonth = '';
    let engagedYear = '';

    if (this.data.engagementMonth && this.data.engagementYear) {
      // Use numeric values from API (engagementMonth: 1-12, engagementYear: 2024)
      engagedMonth = this.getMonthName(this.data.engagementMonth);
      engagedYear = this.data.engagementYear.toString();
    } else if (this.data.engagedSince) {
      // Fall back to parsing string format (e.g., "June 2024")
      const parts = this.data.engagedSince.split(' ');
      if (parts.length === 2) {
        engagedMonth = parts[0];
        engagedYear = parts[1];
      }
    }

    this.form = this.fb.group({
      championName: [this.data.championName || '', Validators.required],
      engagedMonth: [engagedMonth, Validators.required],
      engagedYear: [engagedYear, Validators.required],
      testimonial: [this.data.testimonial || '', Validators.required],
      visibilityDuration: [this.data.visibilityDuration || '12', Validators.required]
    });

    // Disable all fields if not in edit mode (i.e., in resubmit mode)
    if (!this.data.isEditMode) {
      this.form.disable();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.mediaFileName = this.selectedFile.name;
      this.fileSize = this.formatFileSize(this.selectedFile.size);

      // Determine file type
      const mimeType = this.selectedFile.type;
      this.isVideo = mimeType.startsWith('video/');
      this.isDocument = mimeType.startsWith('application/') ||
                        mimeType.includes('pdf') ||
                        mimeType.includes('doc') ||
                        mimeType.includes('xls');

      // Upload file immediately when in edit mode
      if (this.data.isEditMode) {
        this.uploadFileToServer();
      }
    }
  }

  /**
   * Format file size to human readable format
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Upload file to server using /api/v1/users/upload/file endpoint
   */
  private uploadFileToServer(): void {
    if (!this.selectedFile || !this.data.uuid) {
      console.error('No file selected or testimonial UUID missing');
      return;
    }

    this.isUploading = true;

    // Determine file type based on MIME type
    let fileType: string;
    const mimeType = this.selectedFile.type;

    if (mimeType.startsWith('video/')) {
      fileType = ChampionFileType.VIDEO;
    } else if (mimeType.startsWith('application/') || mimeType.includes('pdf') || mimeType.includes('doc') || mimeType.includes('xls')) {
      fileType = ChampionFileType.DOCUMENT;
    } else {
      fileType = ChampionFileType.IMAGE;
    }

    this.gsUserService.uploadChampionFile({
      entityUuid: this.data.uuid,
      fileType: fileType,
      file: this.selectedFile
    }).subscribe({
      next: (response) => {
        this.isUploading = false;
        this.uploadedFileUuid = response.response.uuid;
        this.mediaUrl = response.response.url;
        console.log('File uploaded successfully:', response.response);
      },
      error: (error) => {
        this.isUploading = false;
        console.error('Error uploading file:', error);
        // Reset file selection on error
        this.selectedFile = null;
        this.mediaFileName = '';
        this.fileSize = '';
        // TODO: Show error toast to user
      }
    });
  }

  /**
   * Remove uploaded file
   */
  onRemoveFile(): void {
    this.selectedFile = null;
    this.mediaFileName = '';
    this.fileSize = '';
    this.uploadedFileUuid = '';
    this.mediaUrl = '';
    this.isVideo = false;
    this.isDocument = false;
  }

  onPreview(): void {
    // If a new file is selected, preview it
    if (this.selectedFile) {
      const url = URL.createObjectURL(this.selectedFile);
      window.open(url, '_blank');
    }
    // Otherwise, open the media URL from the API
    else if (this.mediaUrl) {
      window.open(this.mediaUrl, '_blank');
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onSaveAsDraft(): void {
    if (this.form.valid) {
      const result: ResubmitChampionDialogResult = {
        championName: this.form.value.championName,
        engagedSince: `${this.form.value.engagedMonth} ${this.form.value.engagedYear}`,
        testimonial: this.form.value.testimonial,
        visibilityDuration: this.form.value.visibilityDuration,
        mediaFile: this.selectedFile || undefined,
        action: 'draft'
      };
      this.dialogRef.close(result);
    }
  }

  onResubmit(): void {
    // In resubmit mode (isEditMode: false), the form is disabled, so we skip validation
    // In edit mode (isEditMode: true), we check if the form is valid
    const canSubmit = !this.data.isEditMode || this.form.valid;

    // Don't allow submission if file is still uploading
    if (this.isUploading) {
      console.log('Please wait for file upload to complete');
      return;
    }

    if (canSubmit && !this.isSubmitting) {
      this.isSubmitting = true;

      const engagementMonth = this.monthNameToNumber[this.form.value.engagedMonth] || 1;
      const engagementYear = parseInt(this.form.value.engagedYear, 10);
      const validityMonth = parseInt(this.form.value.visibilityDuration, 10);

      // Use uploaded file UUID if available, otherwise use existing media UUID
      const mediaUuid = this.uploadedFileUuid || this.data.mediaUuid || '';

      const request: ResubmitChampionRequest = {
        organisationUuid: this.data.organizationUuid,
        organisationChampionLinkUuid: this.data.uuid,
        championLinkedEntityData: {
          photoOrVideo: {
            uuid: mediaUuid
          },
          engagementMonth,
          engagementYear,
          validityMonth,
          testimonial: this.form.value.testimonial
        }
      };

      this.championTestimonialService.resubmitChampion(request).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.dialogRef.close({ success: true, action: 'resubmit' });
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error resubmitting champion:', error);
          // TODO: Show error toast to user
          this.dialogRef.close({ success: false, error, action: 'resubmit' });
        }
      });
    }
  }
}
